import { memo, useMemo, useState } from 'react';

import type { RelevancyCardProps } from '../DynamicCardComponent';

import { trackCardElement } from '@services/GoogleTagManagerService';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { competitionId, sponsorPhase, competitionCode } from '@utilities/WindowContext';

const cacheBuster = Math.round(+new Date() / (1000 * 60 * 10));

const GamingComponent = ({ item, item: { teaser }, cardFormat }: RelevancyCardProps) => {
  const [additionalTracking, setAdditionalTracking] = useState(null);
  const [isWidget, setIsWidget] = useState(null);

  const attachEvent = (element: HTMLElement) => {
    if (!element || !!additionalTracking) return;

    element.addEventListener(
      'tracking-ready',
      (
        event: Event & {
          detail: {
            id: string;
            type: string;
            subtype: string;
            status: string;
            sponsor: string;
            title: string;
            hasVideo: boolean;
            isWidget: boolean;
          };
        },
      ) => {
        if (!event.detail) return;

        const { type, subtype, id, status, sponsor, title, hasVideo, isWidget } = event.detail;
        const name = `${type?.toUpperCase()}${type?.toLowerCase() === 'got' ? subtype[0]?.toUpperCase() : ''} - ${title} `;
        const category = `${type?.toLowerCase()} poll ${isWidget ? 'widget' : 'card'}`;
        setIsWidget(isWidget);

        setAdditionalTracking({
          id: id,
          category: category,
          name: name,
          brand: competitionCode,
          item_category2: hasVideo ? 'video' : 'image',
          ...(sponsor ? { item_category3: 'sponsored', item_category4: sponsor } : {}),
          item_category5: status?.toLowerCase(),
          item_list_id: id,
        });
      },
    );
  };

  const trackCardElementClick = (e: MouseEvent) => {
    if (e.button > 1 || isWidget) return;
    GoogleTagManagerHelper.gtmCardElementClick(e.target as HTMLElement, {
      ...additionalTracking,
      variant: 'link',
    });
  };

  const scriptSrc = useMemo(() => {
    if (teaser.integrationType !== 'WEBCOMP' || !teaser.source) return;
    const src = new URL(teaser.source);
    src.searchParams.append('_t', `${cacheBuster}`);

    return src.toString();
  }, [teaser.integrationType, teaser.source]);

  const attributes = useMemo(
    () => ({
      ...teaser,
      legNumber: teaser?.context?.leg?.legNumber,
      roundId: teaser?.context?.round?.id,
      matchDayId: teaser?.context?.matchDay?.id,
      pollId: teaser?.context?.pollId,
      nomineeId: teaser?.context?.nomineeId,
      element: null,
      source: null,
      context: null,
      integrationType: null,
    }),
    [teaser],
  );

  const renderContent = () => {
    const Element = teaser.element;

    return (
      <>
        {!!scriptSrc && <script src={scriptSrc} async />}
        {Element && (
          <Element
            {...attributes}
            language={teaser.language}
            cup={competitionId}
            competitionBanner={item.competitionCode.toLocaleLowerCase()}
            cardFormat={cardFormat}
            sponsorPhase={sponsorPhase}
            ref={attachEvent}
            onClick={trackCardElementClick}
          />
        )}
      </>
    );
  };

  return (
    <div className={`card game-card ${item.subType.toLowerCase()}`}>
      {teaser.integrationType === 'IFRAME' && <iframe src={teaser.source} title={teaser.title} />}
      {teaser.integrationType === 'WEBCOMP' && (
        <>
          {additionalTracking ? (
            <div className='pk-d--inline' data-event-label='se' ref={trackCardElement(additionalTracking, true)}>
              {renderContent()}
            </div>
          ) : (
            renderContent()
          )}
        </>
      )}
    </div>
  );
};

export default memo(GamingComponent);
